import { ELEMENT_TYPE } from '@vindr/services/apis/element.api';
import { Checkbox, Form, Input, InputNumber } from 'antd';
import { Fragment, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import InputComponentElement from '../../../InputComponentElement';
import { StructuredReportElement } from '../types';
import GenerateListElement from './GenerateListElement';
import { useGetElementDetail } from '@vindr/services/actions/element.action';
import useRefreshComponent from '@vindr/utils/hooks/useRefreshComponent';

export interface GenerateListProps {
  disabled: boolean;
  element: GenerateListElement;
  onChange: (val: any, changedElement: StructuredReportElement, preventHighlight?: boolean) => void;
}

const GenerateList = ({ disabled, element, onChange }: GenerateListProps) => {
  const { t } = useTranslation('Vindoc');
  const { data, isLoading } = useGetElementDetail(
    { id: element.id },
    { enabled: !!element.linked_element_ids?.length && element.elements.length === 0 }
  );
  const [isHidden, setIsHidden] = useState(element.is_hidden);
  const [refresh, setRefresh] = useRefreshComponent();

  useEffect(() => {
    element.setIsHidden(isHidden);
    onChange(element.value, element);
    setRefresh();
  }, [element, isHidden, onChange, setRefresh]);

  useEffect(() => {
    if (isLoading || !data?.elements?.length || element.elements.length > 0) {
      return;
    }

    const mergeElements = data.elements.map((x, index) => ({ ...x, ...element.elements[index] }));

    element.setElements(mergeElements);
    onChange(element.value, element, true);
    setItemValues(element.item_values);
  }, [data, element, isLoading]);

  const [range, setRange] = useState<number>(
    (Array.isArray(element?.value) ? element?.value?.length : element?.value) ??
      element.default_value ??
      element.item_values?.length ??
      0
  );
  const [itemValues, setItemValues] = useState<typeof element.item_values>(element.item_values);

  const handleChangeItemValue = useCallback(
    (
      itemValue: string,
      idx: number,
      childElement: StructuredReportElement,
      preventHighlight?: boolean
    ) => {
      if (childElement) {
        // console.log(
        //   `📕 childElement.value, childElement - 30:GenerateList.tsx \n`,
        //   childElement.value,
        //   childElement
        // );
        onChange(childElement.value, childElement, preventHighlight);
      } else {
        const updatedValues = element.updateChildValue(itemValue, idx);
        // console.log(`📕 updatedValues - 32:GenerateList.tsx \n`, updatedValues);
        setItemValues([...updatedValues]);
        onChange(element.value, element);
      }
    },
    [element, onChange]
  );
  // useEffect(() => {
  //   console.log(`📕 num - 43:GenerateList.tsx \n`, range);
  // }, [range]);
  const handleChangeValue = useCallback(
    (num: number) => {
      setRange(num);
      const updatedItemValues = element.setValue(num);
      // console.log(`📕 updatedItemValues - 51:GenerateList.tsx \n`, updatedItemValues);
      setItemValues([...updatedItemValues]);
      // onChange(element);
      onChange(num, element);
    },
    [element, onChange]
  );

  return (
    <Fragment key={refresh}>
      <div key={element.id} className="flex items-start">
        <Checkbox
          checked={!isHidden}
          className="mr-2 mt-0.5"
          onChange={(e) => setIsHidden(!e.target.checked)}
        />
        <Form.Item
          key={element.id}
          className="mb-3 flex-1"
          label={element.name}
          tooltip={{ title: element.tooltip }}
          rules={[
            {
              validator: (_, value) => {
                if (Number(value) >= 100 || Number(value) < 0) {
                  return Promise.reject(t('Please enter a number between 0 and 99'));
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          {typeof range === 'number' && (
            <ControlInputNumber
              disabled={disabled}
              element={element}
              range={range}
              onChange={handleChangeValue}
            />
          )}
        </Form.Item>
      </div>
      <div className="ml-3">
        {itemValues?.slice(0, range).map((item: typeof itemValues['0'], idx) => (
          <Item
            key={`${element.id}-${idx}`}
            disabled={disabled}
            element={element}
            handleChangeItemValue={handleChangeItemValue}
            idx={idx}
            item={item}
          />
        ))}
      </div>
    </Fragment>
  );
};

// ? Split into a separate component to ensure InputNumber value clears properly on unmount.
const ControlInputNumber = ({
  range,
  element,
  onChange,
  disabled,
}: {
  range: number;
  disabled: boolean;
  element: GenerateListElement;
  onChange: (num: number) => void;
}) => {
  const { t } = useTranslation('Vindoc');
  const [value, setValue] = useState(range);

  useEffect(() => {
    setValue(range);

    return () => {
      setValue(null);
    };
  }, [range]);

  return (
    <InputNumber
      className="w-full"
      defaultValue={value}
      disabled={!element.is_editable_value || disabled}
      max={99}
      min={0}
      placeholder={t(element.placeholder || 'Provide a quantity')}
      value={value}
      onChange={(value) => onChange(Number(value))}
    />
  );
};

interface ItemProps {
  element: GenerateListElement;
  handleChangeItemValue: (
    itemValue: string,
    idx: number,
    childElement?: StructuredReportElement,
    preventHighlight?: boolean
  ) => void;
  disabled: boolean;
  idx: number;
  item: string | StructuredReportElement;
}

const Item = ({ element, handleChangeItemValue, disabled, idx, item }: ItemProps) => {
  const { t } = useTranslation('Vindoc');

  if (typeof item === 'string') {
    return (
      <Form.Item className="mb-3" label={`${element.detail_label} ${idx + 1}`}>
        <Input
          disabled={!element.is_editable_value || disabled}
          placeholder={t(element?.placeholder || 'Input details')}
          value={item}
          onChange={(e) => handleChangeItemValue(e.target.value, idx)}
        />
      </Form.Item>
    );
  }

  return (
    <>
      {item?.type === ELEMENT_TYPE.GENERATED_LIST && element?.detail_label && (
        <div>{element?.detail_label + ' ' + (idx + 1)}</div>
      )}
      <InputComponentElement
        disabled={!element.is_editable_value || disabled}
        element={item}
        index={idx + 1}
        onChange={(val, element, preventHightlight) =>
          handleChangeItemValue(val, idx, item, preventHightlight)
        }
      />
    </>
  );
};
Item.displayName = 'Item';

export default GenerateList;
